// Libraries
import { action, computed, observable } from "mobx";

// Core
import { ViewModelBase } from "Core/ViewModels/ViewModelBase";
import { FieldType } from "Core/Utils/Utils";

// Custom
import { Server } from "Custom/Globals/AppUrls";
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";

export interface SignatureValidationData {
    isValid: boolean;
    propertyId: string;
    purchaserType: number;
    expiryDate: string;
    daysUntilExpiry: number;
    isSignatureComplete: boolean;
    requestStatus: number;
}

export class SignatureRequestPageViewModel extends ViewModelBase<any> {
    // #region Properties

    @observable
    public apiStatus: ApiStatus = ApiStatus.loading;

    @observable
    public isValidToken: boolean = false;

    @observable
    public validationError: string | null = null;

    @observable
    public propertyId: string | null = null;

    @observable
    public purchaserType: number | null = null;

    @observable
    public expiryDate: string | null = null;

    @observable
    public daysUntilExpiry: number | null = null;

    @observable
    public isSignatureComplete: boolean = false;

    @observable
    public requestStatus: number | null = null;

    // #endregion

    // #region Constructor

    constructor() {
        super({} as any);
    }

    // #endregion

    // #region Computed Properties

    @computed
    public get ApiStatus(): ApiStatus {
        return this.apiStatus;
    }

    // #endregion

    // #region Actions

    @action
    public setApiStatus(apiStatus: ApiStatus) {
        this.apiStatus = apiStatus;
    }

    @action
    public async validateSignatureToken(reservationId: string, signatureToken: string): Promise<void> {
        try {
            this.setApiStatus(ApiStatus.loading);
            this.validationError = null;

            const apiResult = await this.Get<SignatureValidationData>(`${Server.Api.Signature.ValidateSignatureToken}/${reservationId}/${signatureToken}`);

            if (apiResult.wasSuccessful && apiResult.payload) {
                const validationData = apiResult.payload;

                // Wrap all property assignments in a single action
                this.setValidationSuccess(validationData);
                this.setApiStatus(ApiStatus.success);
            } else {
                this.setApiStatus(ApiStatus.error);
                this.setValidationError(apiResult.errors?.[0]?.message || "Failed to validate signature token");
            }
        } catch (error) {
            this.setApiStatus(ApiStatus.error);
            this.setValidationError(error instanceof Error ? error.message : "Failed to validate signature token");
        }
    }

    @action
    private setValidationSuccess(validationData: SignatureValidationData): void {
        this.isValidToken = true;
        this.propertyId = validationData.propertyId;
        this.purchaserType = validationData.purchaserType;
        this.expiryDate = validationData.expiryDate;
        this.daysUntilExpiry = validationData.daysUntilExpiry;
        this.isSignatureComplete = validationData.isSignatureComplete;
        this.requestStatus = validationData.requestStatus;
    }

    @action
    private setValidationError(errorMessage: string): void {
        this.isValidToken = false;
        this.validationError = errorMessage;
    }

    @action
    public reset(): void {
        this.setApiStatus(ApiStatus.loading);
        this.isValidToken = false;
        this.validationError = null;
        this.propertyId = null;
        this.purchaserType = null;
        this.expiryDate = null;
        this.daysUntilExpiry = null;
    }

    // #endregion

    // #region Boilerplate

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    public isFieldValid(fieldName: keyof FieldType<any>): boolean {
        return true;
    }

    // #endregion
}
